<template>
<div>
  <loading :active.sync="loading"></loading>
  <router-view></router-view>
  <!-- Required data warnings -->
  <div v-if="!adminComplete" class="row">
    <router-link to="/profile/business/contacts">
      <b-alert variant="info" show>In order to purchase advertising you must first fill out your administrator credentials.</b-alert>
    </router-link>
  </div>
  <div v-if="!category" class="row">
    <router-link to="/profile/business/details/services">
      <b-alert variant="info" show>In order to create a coupon you must first fill out your business categories.</b-alert>
    </router-link>
  </div>
  <!-- Display if no advertising purchases -->
  <div v-if="!endPath && !getCurrentPlanName && !getCouponId" class="row">
    <div class="relative-positioning col-xl col-md-7">
      <img v-bind:src="advertisingTileIcons.MonthlySubscription">
      <h3 class="tile-title">"Move-On-Up" Subscription</h3>
      <p class="tile-text">Want to move your company to the front of the line? Advertise with HomApt for optimal search listings and start getting qualified leads. Simply choose where you want to advertise and the type of package that will help increase your sales and annual revenues.</p>
      <router-link v-if="adminComplete" tag="button" class="tile-learn-more-button btn btn-primary" to="/profile/business/advertising/priority/overview">Learn More</router-link>
    </div>
    <div class="relative-positioning col-xl col-md-7">
      <img v-bind:src="advertisingTileIcons.PayPerClickIcon">
      <h3 class="tile-title">"Pay-Per-Click" Ads</h3>
      <p class="tile-text">Not ready for a monthly subscription?  Set a monthly budget and only pay when customers click on your coupon or advertising banner.</p>
      <router-link v-if="adminComplete && category" tag="button" class="tile-learn-more-button btn btn-primary" to="/profile/business/advertising/coupons/overview">Learn More</router-link>
    </div>
  </div>
  <!-- Display current advertising purchases -->
  <div v-if="!endPath && getCurrentPlanName">
    <div class="jumbotron">
      <div>
        <div class="row">
          <h3>Current Ad Campaign</h3>
        </div>
        <div class="row">
          <div class="col-2">
            <p class="no-margin-bottom">Auto-Renewal</p>
          </div>
          <div class="col-3 my-auto">
            <p class="d-inline">Off</p>
            <label class="switch" v-on:click="showConfirmModal">
              <input v-model="priorityPlanStatus" value="true" type="checkbox">
              <span class="slider round"></span>
            </label>
            <p class="d-inline">On</p>
          </div>
          <div v-if="!getPriorityPlanStatus" class="col-3 my-auto">
            <p>ends {{ (getPlanBillingPeriodEndDate.getMonth() + 1) + '/' + (getPlanBillingPeriodEndDate.getDate() + 1) + '/' + getPlanBillingPeriodEndDate.getFullYear() }}</p>
          </div>
        </div>
        <div class="row needs-margin-bottom">
          <div class="col my-auto">
            <h5 class="d-inline">Package:</h5>
            <p class="d-inline">{{ getCurrentPlanName }}</p>
          </div>
        </div>
        <div class="row needs-margin-bottom">
          <div class="col my-auto">
            <h5 class="d-inline">Zip Codes:</h5>
            <p v-for="zip in getCurrentPlanZipCodes" :key="zip.key" class="d-inline">{{ zip }}, </p>
          </div>
        </div>
        <div class="row needs-margin-bottom">
          <div class="col my-auto">
            <h5 class="d-inline">Monthly Total:</h5>
            <p class="d-inline">${{ getCurrentPlanPrice }}</p>
          </div>
        </div>
        <div class="row needs-margin-bottom">
          <div class="col-lg-6 offset-lg-3 my-auto">
            <div class="form-check">
              <router-link tag="button" to="/profile/business/advertising/priority/overview" class="btn btn-primary">Edit</router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Display current coupon -->
  <div v-if="!endPath && getCouponId" class="jumbotron">
    <div class="row">
      <div class="col">
        <h3>Coupon Campaign</h3>
      </div>
    </div>
    <div class="row">
      <div class="col-2">
        <p class="no-margin-bottom">Coupon Status</p>
      </div>
      <div class="col my-auto">
        <p class="d-inline">Off</p>
        <label class="switch" v-on:click="showCouponConfirmModal">
          <input v-model="couponStatus" value="true" type="checkbox">
          <span class="slider round"></span>
        </label>
        <p class="d-inline">On</p>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <p>{{ getCouponName }}</p>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-4 my-auto">
        <div class="form-check">
          <router-link v-if="adminComplete" tag="button" class="btn btn-primary edit-button" to="/profile/business/advertising/coupons/overview">Edit</router-link>
        </div>
      </div>
    </div>
  </div>
  <!-- Means of selecting advertising campaign -->
  <div v-if="!endPath && !getCurrentPlanName && getCouponId" class="jumbotron relative-positioning">
    <div class="row">
      <div class="col">
        <h3>Want to move up?</h3>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <p>Want to move your company to the front of the line? Advertise with HomApt for optimal search listings and start getting qualified leads. Simply choose where you want to advertise and the type of package that will help increase your sales and annual revenues.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-6 offset-lg-3 my-auto">
        <div class="form-check">
          <router-link v-if="adminComplete" tag="button" class="btn btn-primary" to="/profile/business/advertising/priority/overview">Get Started</router-link>
        </div>
      </div>
    </div>
  </div>
  <!-- Means of selecting coupon -->
  <div v-if="!endPath && getCurrentPlanName && !getCouponId" class="jumbotron relative-positioning">
    <div class="row">
      <div class="col">
        <h3>Activate Your Free Coupon!</h3>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <p><span v-if="getCurrentPlanName"> Your monthly subscription includes {{ includedFreeClicks }} free coupon clicks. </span>Set a monthly budget and only pay when customers click on your coupon or advertising banner.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-6 offset-lg-3 my-auto">
        <div class="form-check">
          <router-link v-if="adminComplete" tag="button" class="btn btn-primary" to="/profile/business/advertising/coupons/overview">Get Started</router-link>
        </div>
      </div>
    </div>
  </div>
  <!-- Disclaimer information -->
  <div v-if="!endPath && getCurrentPlanName || !endPath && getCouponId" class="jumbotron">
    <div class="row">
      <div class="col">
        <h3>Billing and Refund Policy</h3>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <p>Billing for Subscription Services is completed monthly with the start date of the campaign as the start of the billing cycle. If you discontinue the monthly auto renewal, your advertising campaign will stay active for the remainder of the current period. View current <span class="text-primary" v-on:click="showPricingModal">Pricing</span> information.</p>
        <p>Billing for Coupon Services is invoiced at the beginning of each month for the previous months transactions. If you discontinue the Coupon, you will receive your final invoice at the beginning of the next month for any transactions that occurred before the Coupon was made inactive.</p>
      </div>
    </div>
  </div>
  <b-modal id="pricing-modal" ref="pricingModal" size="lg" hide-footer>
    <div slot="modal-header" class="w-100 justify-content-center post-modal-header">
      <h3>Pricing Information</h3>
      <button v-on:click="hidePricingModal" class="close d-inline" type="button" aria-label="Close">x</button>
    </div>
    <div class="container">
    <div class="row">
      <div class="col-lg-5 my-auto">
        <p>For volume discounts call:<br>(612) 655-2324</p>
      </div>
      <div class="col-lg-7">
        <table class="table-responsive-sm table-bordered">
          <thead>
            <tr>
              <th class="centered-table-content" scope="col"># of Zips Included</th>
              <th class="centered-table-content" scope="col">Price/Month BASIC</th>
              <th class="centered-table-content" scope="col">Price/Month PRO</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="zipPackage in zipPackages" :key="zipPackage.key">
              <td class="centered-table-content">{{ zipPackage.size }}</td>
              <td class="centered-table-content">${{ zipPackage.basic }}</td>
              <td class="centered-table-content">${{ zipPackage.pro }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <table class="table-responsive-sm table-bordered">
          <thead>
            <tr>
              <th class="invisible-table" scope="col" style="width:70%"></th>
              <th scope="col" style="width:10%" class="centered-table-content">FREE</th>
              <th scope="col" style="width:10%" class="centered-table-content">BASIC</th>
              <th scope="col" style="width:10%" class="centered-table-content">PRO</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Update business information</td>
              <td class="centered-table-content">&#10003;</td>
              <td class="centered-table-content">&#10003;</td>
              <td class="centered-table-content">&#10003;</td>
            </tr>
            <tr>
              <td>Add photos</td>
              <td class="centered-table-content">&#10003;</td>
              <td class="centered-table-content">&#10003;</td>
              <td class="centered-table-content">&#10003;</td>
            </tr>
            <tr>
              <td>Free coupon clicks per month</td>
              <td class="centered-table-content"></td>
              <td class="centered-table-content">2</td>
              <td class="centered-table-content">5</td>
            </tr>
            <tr>
              <td>Priority display in searches</td>
              <td class="centered-table-content"></td>
              <td class="centered-table-content">&#10003;</td>
              <td class="centered-table-content">&#10003;</td>
            </tr>
            <tr>
              <td>Highest priority display in searches</td>
              <td class="centered-table-content"></td>
              <td class="centered-table-content"></td>
              <td class="centered-table-content">&#10003;</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    </div>
  </b-modal>
  <b-modal id="confirmModal" v-on:hide="modalClosed" ref="confirmModal" size="md" hide-footer>
    <div slot="modal-header" class="w-100 justify-content-center confirm-modal-header">
      <h3 v-if="!priorityPlanStatus">Confirm Auto-Renewal Cancelation</h3>
      <h3 v-else>Confirm Enable Auto-Renewal</h3>
      <button v-on:click="hideConfirmModal" class="close d-inline" type="button" aria-label="Close">x</button>
    </div>
    <div class="container">
      <p v-if="!priorityPlanStatus">Are you sure you want to cancel auto-renewal?</p>
      <p v-else>Are you sure you want to enable auto-renewal?</p>
      <div class="row">
        <div class="col">
          <button v-on:click="confirmChange" class="btn btn-primary">Confirm</button>
        </div>
      </div>
    </div>
  </b-modal>
  <b-modal id="confirmCouponModal" v-on:hide="couponModalClosed" ref="confirmCouponModal" size="md" hide-footer>
    <div slot="modal-header" class="w-100 justify-content-center confirm-modal-header">
      <h3 v-if="!couponStatus">Confirm Coupon Cancelation</h3>
      <h3 v-else>Confirm Enable Coupon</h3>
      <button v-on:click="hideConfirmCouponModal" class="close d-inline" type="button" aria-label="Close">x</button>
    </div>
    <div class="container">
      <p v-if="!couponStatus">Are you sure you want to cancel your coupon?</p>
      <p v-else>Are you sure you want to enable your coupon?</p>
      <div class="row">
        <div class="col">
          <button v-on:click="confirmCouponChange" class="btn btn-primary">Confirm</button>
        </div>
      </div>
    </div>
  </b-modal>
</div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import { mapActions, mapGetters, mapMutations } from 'vuex'

export default {
  data () {
    return {
      endPath: null,
      loading: false,
      includedFreeClicks: null,
      couponConfirmed: false,
      confirmed: false,
      zipPackages: []
    }
  },
  components: {
    Loading
  },
  watch: {
    '$route' (to, from) {
      this.setEndPath()
    }
  },
  computed: {
    ...mapGetters('business', [
      'adminComplete',
      'getBusinessId',
      'getSelectedServices'
    ]),
    ...mapGetters('products', [
      'getCurrentPlanName',
      'getCurrentPlanPrice',
      'getCurrentPlanZipCodes',
      'getCouponId',
      'getCouponStatus',
      'getCouponName',
      'getPlanBillingPeriodEndDate',
      'getPlanNextBillingDate',
      'getPriorityPlanStatus'
    ]),
    priorityPlanStatus: {
      get () {
        return this.getPriorityPlanStatus
      },
      set (value) {
        this.updatePriorityPlanStatus(value)
      }
    },
    category () {
      return this.getSelectedServices.length
    },
    couponStatus: {
      get () {
        return this.getCouponStatus
      },
      set (value) {
        this.updateCouponStatus(value)
      }
    },
    advertisingTileIcons () {
      var imageNames = ['MonthlySubscription.jpg', 'PayPerClickIcon.jpg']
      var baseUrl = 'https://s3.' +
                     window.CONFIG.Region + '.amazonaws.com/' +
                     window.CONFIG.PhotoBucketName +
                     '/xsmall/uploads/'
      var returnValues = {}
      for (var index in imageNames) {
        var title = imageNames[index].split('.')[0]
        returnValues[title] = baseUrl + imageNames[index]
      }
      return returnValues
    },
    priorityAdvertisingStyle () {
      return {
        background: 'url(' + this.advertisingTileIcons.MonthlySubscription + ')'
      }
    },
    couponAdvertisingStyle () {
      return {
        background: 'url(' + this.advertisingTileIcons.PayPerClickIcon + ')'
      }
    }
  },
  methods: {
    async confirmCouponChange () {
      this.couponConfirmed = true
      this.changeCouponStatus()
      this.hideConfirmCouponModal()
    },
    hideConfirmCouponModal () {
      this.$refs.confirmCouponModal.hide()
    },
    couponModalClosed () {
      if (!this.couponConfirmed) this.couponStatus = !this.couponStatus
      this.couponConfirmed = false
    },
    showCouponConfirmModal () {
      this.$refs.confirmCouponModal.show()
    },

    async confirmChange () {
      this.confirmed = true
      await this.changePriorityPlanStatus()
      this.hideConfirmModal()
    },
    hideConfirmModal () {
      this.$refs.confirmModal.hide()
    },
    modalClosed () {
      if (!this.confirmed) this.priorityPlanStatus = !this.priorityPlanStatus
      this.confirmed = false
    },
    showConfirmModal () {
      console.log(this.priorityPlanStatus)
      this.$refs.confirmModal.show()
    },
    ...mapActions('products', [
      'getCoupons',
      'getPriorityAdvertisingPlan',
      'switchCouponStatus',
      'switchPriorityPlanExpiringStatus'
    ]),
    ...mapMutations('products', [
      'updateCouponStatus',
      'updatePriorityPlanStatus'
    ]),
    async getAdvertisingPrices () {
      try {
        if (this.zipPackages.length > 0) return
        var that = this
        var client = this.$store.getters.getApigClientFactory
        var params = {}
        var pathTemplate = '/advertising'
        var method = 'GET'
        var additionalParams = {}
        var body = {}
        var result = await client.invokeApi(params, pathTemplate, method, additionalParams, body)
        var priceCalculator = result.data.advertising.price_calculator
        for (var i = 0; i < priceCalculator.bundles_size.length; i++) {
          var zipPackage = {
            size: priceCalculator.bundles_size[i],
            basic: priceCalculator.tiers.basic[i],
            pro: priceCalculator.tiers.pro[i]
          }
          that.zipPackages.push(zipPackage)
        }
      } catch (error) {
        console.log(error)
      }
    },
    hidePricingModal () {
      this.$refs.pricingModal.hide()
    },
    showPricingModal () {
      this.getAdvertisingPrices()
      this.$refs.pricingModal.show()
    },
    setEndPath () {
      var pathList = (this.$route.path).split('/')
      this.endPath = pathList[4]
    },
    async changePriorityPlanStatus () {
      this.loading = true
      await this.switchPriorityPlanExpiringStatus()
      this.loading = false
    },
    async changeCouponStatus () {
      this.loading = true
      await this.switchCouponStatus({ businessId: this.getBusinessId })
      this.loading = false
    }
  },
  async created () {
    this.setEndPath()
    await Promise.all([this.getPriorityAdvertisingPlan(), this.getCoupons({ businessId: 'self' })])
  }

}
</script>

<style scoped>
.text-primary {
  cursor: pointer;
}

.table-responsive-sm {
  border: 0px !important;
  margin-bottom: 1rem;
  width: 100%;
}

.invisible-table {
  background-color: white !important;
  border: 0px !important;
}

th {
  background-color: #333333;
  color: #feedaa !important;
}

td {
  background-color: white;
  padding-left: 4px;
  padding-right: 4px;
}

.centered-table-content {
  text-align: center;
}

.close {
  position: absolute;
  text-shadow: none;
  right: 1rem;
  top: 1rem;
  opacity: 1;
}

.black-underline {
  border-bottom: solid;
}

input:checked + .slider {
  background-color: #FEEDAA;
}

input:checked + .slider:before {
  -webkit-transform: translateX(26px);
  -ms-transform: translateX(26px);
  transform: translateX(26px);
}

.jumbotron {
  background-color: #f1f1f1 !important;
  margin-bottom: 2rem !important;
  border-radius: 25px;
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.needs-margin-bottom {
  margin-bottom: 1rem;
}

.relative-positioning {
  position: relative;
}

.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: .4s;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  -webkit-transition: .4s;
  transition: .4s;
}

/* Rounded sliders */
.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

.tile-title {
  position: absolute;
  top: 2vh;
  left: 50%;
  transform: translate(-50%, 0%);
  text-align: center;
  width: 80%;
  color: var(--secondary-background-color) !important;
}

.tile-text {
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: left;
  width: 80%;
  color: white !important;
}

.btn-secondary {
  width: 100% !important;
}

.tile-learn-more-button {
  position: relative;
  bottom: 2vh;
  left: 50%;
  transform: translate(-50%, -100%);
  width: auto !important;
}

.yellow-underline {
  border-bottom: solid;
  border-color: #feedaa;
}
</style>



// WEBPACK FOOTER //
// src/components/profile/businessProfile/advertising/HomaptAdvertising.vue